import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, CreditCard, Loader2, Download } from "lucide-react";
import { api, API, fmtMoney } from "@/lib/api";
import { VENMO_USERNAME } from "@/lib/siteData";

export default function InvoicePay() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/public/invoices/${token}`)
      .then((r) => setInv(r.data))
      .catch(() => setError(true));
  }, [token]);

  const pay = async (option) => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        token, payment_option: option, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  if (error) return <Centered>Invoice not found.</Centered>;
  if (!inv) return <Centered><Loader2 className="animate-spin text-gold" /></Centered>;

  const paid = inv.status === "paid";
  const showDeposit = inv.deposit_amount > 0 && inv.amount_paid === 0 && !paid;
  const dueDollars = ((inv.balance ?? inv.total) / 100).toFixed(2);
  const venmoUser = inv.venmo_username || VENMO_USERNAME;
  const venmoLink = `https://venmo.com/?txn=pay&recipients=${venmoUser}&amount=${dueDollars}&note=${encodeURIComponent("Invoice " + inv.number + " - B3 Contracting")}`;

  return (
    <div className="min-h-screen bg-ink text-white font-body py-10 px-5">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <span className="bg-white rounded-md p-1.5"><img src="/logo-white.png" alt="B3 Contracting LLC" className="h-14 w-auto object-contain" /></span>
          <div className="text-right">
            <p className="font-heading text-gold text-lg">{inv.number}</p>
            <p className="text-xs text-white/45">{new Date(inv.created_at).toLocaleDateString()}</p>
          </div>
        </div>

        <div className="bg-surface border border-line p-7 sm:p-9" data-testid="invoice-view">
          <p className="text-xs uppercase tracking-wider text-white/45">Billed to</p>
          <p className="mt-1 font-heading text-white text-xl">{inv.customer_name}</p>
          {inv.customer_address && <p className="text-white/50 text-sm">{inv.customer_address}</p>}

          <div className="mt-8 divide-y divide-line/60">
            {inv.line_items.map((li, i) => (
              <div key={i} className="flex justify-between py-3 text-sm">
                <span className="text-white/80">{li.description} <span className="text-white/40">× {li.quantity}</span></span>
                <span className="text-white/90">{fmtMoney(Math.round(li.unit_price * li.quantity * 100))}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between text-white/60"><span>Subtotal</span><span>{fmtMoney(inv.subtotal)}</span></div>
            {inv.tax_amount > 0 && <div className="flex justify-between text-white/60"><span>Tax ({inv.tax_rate}%)</span><span>{fmtMoney(inv.tax_amount)}</span></div>}
            <div className="flex justify-between text-white font-semibold text-lg pt-3 border-t border-line"><span>Total</span><span className="text-gold">{fmtMoney(inv.total)}</span></div>
            {inv.amount_paid > 0 && (
              <>
                <div className="flex justify-between text-green-400"><span>Paid</span><span>−{fmtMoney(inv.amount_paid)}</span></div>
                <div className="flex justify-between text-white font-semibold"><span>Balance due</span><span>{fmtMoney(inv.balance)}</span></div>
              </>
            )}
          </div>

          {inv.notes && <p className="mt-6 text-sm text-white/50 border-t border-line pt-4">{inv.notes}</p>}

          <a
            href={`${API}/public/invoices/${token}/pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-pdf"
            className="mt-6 inline-flex items-center gap-2 text-sm text-white/60 hover:text-gold transition-colors"
          >
            <Download size={16} /> Download PDF
          </a>
        </div>

        {paid ? (
          <div data-testid="invoice-paid" className="mt-6 bg-green-500/10 border border-green-500/30 text-green-400 p-6 text-center font-heading text-lg">
            Paid in full — thank you!
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {showDeposit && (
              <button data-testid="pay-deposit" disabled={paying} onClick={() => pay("deposit")} className="w-full flex items-center justify-center gap-2 border border-gold text-gold font-semibold px-8 py-4 hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                Pay Deposit — {fmtMoney(inv.deposit_amount)}
              </button>
            )}
            <button data-testid="pay-full" disabled={paying} onClick={() => pay("full")} className="w-full flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-8 py-4 hover:bg-gold-hover transition-colors disabled:opacity-60">
              {paying ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
              {inv.amount_paid > 0 ? `Pay Balance — ${fmtMoney(inv.balance)}` : `Pay ${fmtMoney(inv.total)}`}
            </button>
            <p className="flex items-center justify-center gap-2 text-xs text-white/45 pt-1">
              <ShieldCheck size={14} className="text-gold" />
              Secure checkout · Card, Apple/Google Pay & Affirm financing available
            </p>

            <div className="flex items-center gap-3 py-2">
              <span className="h-px flex-1 bg-line" />
              <span className="text-xs uppercase tracking-wider text-white/35">or</span>
              <span className="h-px flex-1 bg-line" />
            </div>

            <a
              href={venmoLink}
              target="_blank"
              rel="noreferrer"
              data-testid="pay-venmo"
              className="w-full flex items-center justify-center gap-2 border border-[#3D95CE] text-[#3D95CE] font-semibold px-8 py-4 hover:bg-[#3D95CE] hover:text-white transition-colors"
            >
              Pay with Venmo — @{venmoUser}
            </a>
            <p className="text-center text-xs text-white/40">
              Paying by Venmo, cash, or check? Send it to <span className="text-white/70">@{venmoUser}</span> and
              B3 Contracting will mark your invoice as paid once received.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen bg-ink text-white/70 font-body flex items-center justify-center">{children}</div>;
}
